import React, { useEffect, useState } from 'react';
import { Box, Button, useTheme } from '@mui/material';
import type { SxProps, Theme } from '@mui/material';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import NotificationsOffIcon from '@mui/icons-material/NotificationsOff';
import { useNotifications } from '../contexts/NotificationContext';
import { useAuth } from '../context/AuthContext';

interface EnablePushButtonProps {
  sx?: SxProps<Theme>;
}

/**
 * Animated "pill" CTA that opens the shared admin push opt-in dialog
 * (<EnablePushDialog/>, mounted once in NotificationProvider).
 *
 * Renders only when push is supported/configured, the admin is logged in, and the
 * permission is still 'default' or previously 'denied'. Hidden once granted.
 */
const EnablePushButton: React.FC<EnablePushButtonProps> = ({ sx }) => {
  const theme = useTheme();
  const { user } = useAuth();
  const { pushPermission, pushSupported, pushConfigured, openPushDialog } = useNotifications();

  const [mounted, setMounted] = useState(false);
  useEffect(() => setMounted(true), []);

  const isBlocked = pushPermission === 'denied';
  if (
    !mounted ||
    !user ||
    !pushConfigured ||
    !pushSupported ||
    (pushPermission !== 'default' && !isBlocked)
  ) {
    return null;
  }

  const grad = `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`;
  const gradWarn = `linear-gradient(135deg, ${theme.palette.warning.main} 0%, ${theme.palette.warning.dark} 100%)`;

  return (
    <Button
      onClick={openPushDialog}
      disableElevation
      startIcon={isBlocked ? <NotificationsOffIcon /> : <NotificationsActiveIcon />}
      aria-label="فعال‌سازی اعلان‌های مرورگر"
      sx={{
        flexShrink: 0,
        borderRadius: 999,
        px: { xs: 1.4, sm: 2 },
        py: 0.7,
        minHeight: 36,
        color: '#fff',
        fontWeight: 800,
        fontSize: { xs: '.72rem', sm: '.8rem' },
        whiteSpace: 'nowrap',
        textTransform: 'none',
        background: isBlocked ? gradWarn : grad,
        boxShadow: `0 6px 16px -6px ${(isBlocked ? theme.palette.warning : theme.palette.primary).main}aa`,
        transition: 'transform .18s ease, box-shadow .18s ease, filter .18s ease',
        '& .MuiButton-startIcon': { mr: { xs: 0.3, sm: 0.6 }, ml: { xs: -0.2, sm: 0 } },
        '&:hover': {
          background: isBlocked ? gradWarn : grad,
          filter: 'brightness(1.05)',
          transform: 'translateY(-1px)',
          boxShadow: `0 9px 22px -8px ${(isBlocked ? theme.palette.warning : theme.palette.primary).main}`,
        },
        '&:active': { transform: 'translateY(0) scale(0.98)' },
        ...(isBlocked
          ? {}
          : {
              '& .MuiButton-startIcon .MuiSvgIcon-root': {
                transformOrigin: 'top center',
                animation: 'enablePushRing 2.8s ease-in-out infinite',
              },
              '@keyframes enablePushRing': {
                '0%': { transform: 'rotate(0)' },
                '4%': { transform: 'rotate(18deg)' },
                '8%': { transform: 'rotate(-16deg)' },
                '12%': { transform: 'rotate(14deg)' },
                '16%': { transform: 'rotate(-10deg)' },
                '20%': { transform: 'rotate(6deg)' },
                '24%': { transform: 'rotate(-3deg)' },
                '28%': { transform: 'rotate(0)' },
                '100%': { transform: 'rotate(0)' },
              },
              '@media (prefers-reduced-motion: reduce)': {
                '& .MuiButton-startIcon .MuiSvgIcon-root': { animation: 'none' },
              },
            }),
        ...sx,
      }}
    >
      <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>
        {isBlocked ? 'رفع مسدودی اعلان' : 'فعال‌سازی اعلان‌ها'}
      </Box>
      <Box component="span" sx={{ display: { xs: 'inline', sm: 'none' } }}>
        اعلان‌ها
      </Box>
    </Button>
  );
};

export default EnablePushButton;
